/**
 * Wallet Service
 * Handles wallet and tipping API calls
 */

import api from './api';
import { getAxiosErrorMessage } from '../utils/error';
import { serverErrorMessage } from '../utils/extractApiError';
import { BUILD_CHAIN_ID } from '../config/chain';

// ============================================
// Wallet Types
// ============================================

export interface Wallet {
  lifetime_earned: string | number;
  lifetime_given: string | number;
  has_wallet: boolean;
  wallet_address: string | null;
}

type TransactionType = 'tip_received' | 'tip_sent';

export interface WalletTransaction {
  id: number;
  type: TransactionType;
  amount: string | number;
  note?: string;
  created_at: string;
  session_id?: number;
  is_onchain?: boolean;
  tx_hash?: string;
}

interface Pagination {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface TippingStats {
  total_tips_sent: number;
  total_tips_received: number;
  total_amount_sent: number;
  total_amount_received: number;
  [key: string]: unknown;
}

// ============================================
// Response Types
// ============================================

interface GetWalletSuccessResponse {
  success: true;
  wallet: Wallet;
}

interface GetWalletErrorResponse {
  success: false;
  error: string;
}

type GetWalletResponse = GetWalletSuccessResponse | GetWalletErrorResponse;

interface GetTransactionsSuccessResponse {
  success: true;
  transactions: WalletTransaction[];
  pagination: Pagination;
}

interface GetTransactionsErrorResponse {
  success: false;
  error: string;
}

type GetTransactionsResponse = GetTransactionsSuccessResponse | GetTransactionsErrorResponse;

interface GetTippingStatsSuccessResponse {
  success: true;
  stats: TippingStats;
}

interface GetTippingStatsErrorResponse {
  success: false;
  error: string;
}

type GetTippingStatsResponse = GetTippingStatsSuccessResponse | GetTippingStatsErrorResponse;

// Use shared error utility with built-in logging
const handleAxiosError = (error: unknown, defaultMessage: string): string => {
  return getAxiosErrorMessage(error, defaultMessage, { log: 'WalletService' });
};

// ============================================
// Wallet Functions
// ============================================

/**
 * Get user's wallet information
 */
export const getWallet = async (): Promise<GetWalletResponse> => {
  try {
    const response = await api.get('/wallet');
    return { success: true, wallet: response.data.wallet };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to fetch wallet') };
  }
};

/**
 * Get wallet transactions with pagination
 */
export const getTransactions = async (
  page: number = 1,
  limit: number = 20,
): Promise<GetTransactionsResponse> => {
  try {
    const response = await api.get(`/wallet/transactions?page=${page}&limit=${limit}`);
    return {
      success: true,
      transactions: response.data.transactions,
      pagination: response.data.pagination,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to fetch transactions') };
  }
};

/**
 * Get tipping statistics
 */
export const getTippingStats = async (): Promise<GetTippingStatsResponse> => {
  try {
    const response = await api.get('/wallet/tipping-stats');
    return { success: true, stats: response.data.stats };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to fetch stats') };
  }
};

// ============================================
// Coinbase Onramp
// ============================================

interface OnrampSessionSuccessResponse {
  success: true;
  url: string;
  is_sandbox: boolean;
  wallet_address: string;
}

interface OnrampSessionErrorResponse {
  success: false;
  error: string;
}

type OnrampSessionResponse = OnrampSessionSuccessResponse | OnrampSessionErrorResponse;

/**
 * Generate a Coinbase Onramp session URL.
 * This creates a secure, one-time-use token for the buy flow.
 *
 * @param walletAddress Optional wallet address from frontend to verify against stored address
 */
export const getOnrampSession = async (walletAddress?: string): Promise<OnrampSessionResponse> => {
  try {
    const response = await api.post(
      '/wallet/onramp/session',
      walletAddress ? { wallet_address: walletAddress } : {},
    );
    return {
      success: true,
      url: response.data.url,
      is_sandbox: response.data.is_sandbox,
      wallet_address: response.data.wallet_address,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to generate onramp session') };
  }
};

// ============================================
// Pending Transactions (Dual-Check System)
// ============================================

export type PendingTransactionStatus = 'awaiting_confirmation' | 'confirmed' | 'failed' | 'expired';

interface PendingTransaction {
  id: number;
  status: PendingTransactionStatus;
  amount: number;
  tx_hash: string | null;
  confirmed_at: string | null;
  error_message: string | null;
}

/**
 * Who a tip pays, as the SERVER names it from the session (c-125): never the
 * id the app's store holds. `wallet_address` (pending answer only) is the
 * row's `to_address`, the address the server verifies the transfer against.
 */
export interface TipRecipient {
  id: number | null;
  name: string | null;
  wallet_address?: string | null;
}

interface CreatePendingTipSuccessResponse {
  success: true;
  pending_id: number;
  status: PendingTransactionStatus;
  /** Absent only from a server older than c-125: the modal then refuses to send. */
  recipient: TipRecipient | null;
  message: string;
}

interface CreatePendingTipErrorResponse {
  success: false;
  error: string;
}

type CreatePendingTipResponse = CreatePendingTipSuccessResponse | CreatePendingTipErrorResponse;

interface GetPendingStatusSuccessResponse {
  success: true;
  pending: PendingTransaction;
}

interface GetPendingStatusErrorResponse {
  success: false;
  error: string;
}

type GetPendingStatusResponse = GetPendingStatusSuccessResponse | GetPendingStatusErrorResponse;

/**
 * Create a pending tip transaction.
 * Call this BEFORE initiating the on-chain transaction.
 * The pending record will be matched with the CDP webhook for confirmation.
 *
 * No recipient is sent: the server derives who the tip pays from the session
 * and answers with that person and the address to pay (c-125).
 */
export const createPendingTip = async (
  sessionId: number,
  amount: number,
): Promise<CreatePendingTipResponse> => {
  try {
    // chain_id: the server refuses any chain but its own before a row
    // exists, so a build on the wrong network never sends (#1165).
    const response = await api.post('/tips/pending', {
      session_id: sessionId,
      amount,
      chain_id: BUILD_CHAIN_ID,
    });
    return {
      success: true,
      pending_id: response.data.pending_id,
      status: response.data.status,
      recipient: response.data.recipient ?? null,
      message: response.data.message,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to create pending tip') };
  }
};

/**
 * Get the status of a pending tip.
 */
export const getPendingTipStatus = async (pendingId: number): Promise<GetPendingStatusResponse> => {
  try {
    const response = await api.get(`/tips/pending/${pendingId}`);
    return { success: true, pending: response.data.pending };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get pending tip status') };
  }
};

interface ConfirmPendingResponse {
  success: boolean;
  message?: string;
  error?: string;
  /**
   * false (HTTP 202) when the server has not verified the transfer on-chain
   * yet; it re-checks on its own and `pending.status` stays awaiting.
   */
  verified?: boolean;
  code?: string;
  pending?: {
    id: number;
    status: PendingTransactionStatus;
    tx_hash: string;
    confirmed_at: string;
  };
}

/**
 * Confirm a pending tip with the transaction hash.
 * Call this AFTER the on-chain transaction succeeds.
 */
export const confirmPendingTip = async (
  pendingId: number,
  txHash: string,
): Promise<ConfirmPendingResponse> => {
  try {
    const response = await api.post(`/tips/pending/${pendingId}/confirm`, {
      tx_hash: txHash,
      chain_id: BUILD_CHAIN_ID,
    });
    return {
      success: true,
      message: response.data.message,
      verified: response.data.verified ?? true,
      code: response.data.code,
      pending: response.data.pending,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to confirm tip') };
  }
};

interface CreatePendingWithdrawalSuccessResponse {
  success: true;
  pending_id: number;
  status: PendingTransactionStatus;
  message: string;
}

interface CreatePendingWithdrawalErrorResponse {
  success: false;
  error: string;
}

type CreatePendingWithdrawalResponse =
  | CreatePendingWithdrawalSuccessResponse
  | CreatePendingWithdrawalErrorResponse;

/**
 * Create a pending withdrawal transaction.
 * Call this BEFORE initiating the on-chain transaction.
 * The pending record will be matched with the CDP webhook for confirmation.
 */
export const createPendingWithdrawal = async (
  destinationAddress: string,
  amount: number,
): Promise<CreatePendingWithdrawalResponse> => {
  try {
    const response = await api.post('/withdrawals/pending', {
      destination_address: destinationAddress,
      amount,
      chain_id: BUILD_CHAIN_ID,
    });
    return {
      success: true,
      pending_id: response.data.pending_id,
      status: response.data.status,
      message: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: handleAxiosError(error, 'Failed to create pending withdrawal'),
    };
  }
};

/**
 * Get the status of a pending withdrawal.
 */
export const getPendingWithdrawalStatus = async (
  pendingId: number,
): Promise<GetPendingStatusResponse> => {
  try {
    const response = await api.get(`/withdrawals/pending/${pendingId}`);
    return { success: true, pending: response.data.pending };
  } catch (error) {
    return {
      success: false,
      error: handleAxiosError(error, 'Failed to get pending withdrawal status'),
    };
  }
};

/**
 * Confirm a pending withdrawal with the transaction hash.
 * Call this AFTER the on-chain transaction succeeds.
 */
export const confirmPendingWithdrawal = async (
  pendingId: number,
  txHash: string,
): Promise<ConfirmPendingResponse> => {
  try {
    const response = await api.post(`/withdrawals/pending/${pendingId}/confirm`, {
      tx_hash: txHash,
      chain_id: BUILD_CHAIN_ID,
    });
    return {
      success: true,
      message: response.data.message,
      verified: response.data.verified ?? true,
      code: response.data.code,
      pending: response.data.pending,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to confirm withdrawal') };
  }
};

// ============================================
// Pending Transactions List
// ============================================

export type PendingTransactionType = 'tip' | 'withdrawal';

export interface PendingTransactionItem {
  id: number;
  type: PendingTransactionType;
  status: PendingTransactionStatus;
  amount: number;
  recipient_id: number | null;
  recipient_name: string | null;
  session_id: number | null;
  destination_address: string | null;
  tx_hash: string | null;
  confirmed_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface GetPendingTransactionsSuccessResponse {
  success: true;
  transactions: PendingTransactionItem[];
}

interface GetPendingTransactionsErrorResponse {
  success: false;
  error: string;
}

type GetPendingTransactionsResponse =
  | GetPendingTransactionsSuccessResponse
  | GetPendingTransactionsErrorResponse;

/**
 * Get all pending transactions (tips and withdrawals) for the current user.
 */
export const getPendingTransactions = async (): Promise<GetPendingTransactionsResponse> => {
  try {
    const response = await api.get('/wallet/pending-transactions');
    return { success: true, transactions: response.data.transactions };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get pending transactions') };
  }
};

// ============================================
// Wallet Configuration
// ============================================

export interface WalletConfig {
  chain_id: number;
  is_testnet: boolean;
  protocol_fee: number;
  min_tip_amount: number;
  min_withdrawal_amount: number;
}

interface GetWalletConfigSuccessResponse {
  success: true;
  config: WalletConfig;
}

interface GetWalletConfigErrorResponse {
  success: false;
  error: string;
}

type GetWalletConfigResponse = GetWalletConfigSuccessResponse | GetWalletConfigErrorResponse;

/**
 * Get wallet configuration from the backend.
 * Used to verify frontend and backend are on the same network.
 */
export const getWalletConfig = async (): Promise<GetWalletConfigResponse> => {
  try {
    const response = await api.get('/wallet/config');
    return { success: true, config: response.data.config };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get wallet config') };
  }
};

// ============================================
// CDP Embedded Wallet JWT Authentication
// ============================================

interface GetCdpJwtSuccessResponse {
  success: true;
  token: string;
  expires_at: number;
}

interface GetCdpJwtErrorResponse {
  success: false;
  error: string;
}

type GetCdpJwtResponse = GetCdpJwtSuccessResponse | GetCdpJwtErrorResponse;

/**
 * Get a JWT token for CDP Embedded Wallet authentication.
 * This token is used by the CDP SDK to authenticate the user
 * and create/access their embedded wallet.
 */
export const getCdpJwt = async (): Promise<GetCdpJwtResponse> => {
  try {
    const response = await api.post('/wallet/embedded/jwt');
    return {
      success: true,
      token: response.data.token,
      expires_at: response.data.expires_at,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get CDP JWT') };
  }
};

// ============================================
// Embedded Wallet Registration
// ============================================

interface RegisterEmbeddedWalletSuccessResponse {
  success: true;
  message: string;
  wallet_address: string;
  created_at: string | null;
}

interface RegisterEmbeddedWalletErrorResponse {
  success: false;
  error: string;
}

type RegisterEmbeddedWalletResponse =
  | RegisterEmbeddedWalletSuccessResponse
  | RegisterEmbeddedWalletErrorResponse;

/**
 * Signs a message with the embedded wallet (EIP-191 personal_sign) and returns
 * the 0x signature. Supplied by the caller (useCdpAuth().signMessage) so this
 * module, which non-wallet pages import, never imports the CDP SDK itself.
 */
export type WalletMessageSigner = (address: string, message: string) => Promise<string>;

/**
 * Link the embedded wallet to the signed-in account, with proof of ownership.
 *
 * The server hands out a one-time nonce and the exact message to sign; the
 * wallet signs it; the server recovers the signer and links the address only
 * if it is the one claimed. The app's CDP embedded wallets are EOAs
 * (`createOnLogin: 'eoa'`), so an EIP-191 signature recovers their address.
 * There is no unsigned path: the old `register-simple` endpoint trusted the
 * address the client sent (#940 reviewer finding 2) and is gone.
 *
 * @param walletAddress - The wallet address to register (from CDP)
 * @param sign - Signs the server's message with that wallet
 */
export const registerEmbeddedWallet = async (
  walletAddress: string,
  sign: WalletMessageSigner,
): Promise<RegisterEmbeddedWalletResponse> => {
  try {
    const challenge = await api.get('/wallet/embedded/nonce');
    const nonce: string = challenge.data.nonce;
    const message: string = challenge.data.message;
    const signature = await sign(walletAddress, message);
    const response = await api.post('/wallet/embedded/register', {
      wallet_address: walletAddress,
      nonce,
      signature,
    });
    return {
      success: true,
      message: response.data.message,
      wallet_address: response.data.wallet_address,
      created_at: response.data.created_at,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to register wallet') };
  }
};

interface GetEmbeddedRecipientWalletSuccessResponse {
  success: true;
  has_wallet: true;
  wallet_address: string;
  /** The session's other participant, as the server derived them (c-125). */
  recipient: TipRecipient | null;
}

interface GetEmbeddedRecipientWalletErrorResponse {
  success: false;
  error: string;
  /** `false` ONLY on the server's "no wallet yet" answer; absent on any other refusal. */
  has_wallet?: boolean;
  /** Present with `has_wallet: false`: the partner who has no wallet yet. */
  recipient?: TipRecipient | null;
  /** The HTTP status of the refusal; absent when no response arrived (network error). */
  status?: number;
}

type GetEmbeddedRecipientWalletResponse =
  | GetEmbeddedRecipientWalletSuccessResponse
  | GetEmbeddedRecipientWalletErrorResponse;

/**
 * Who a tip on this session pays, and their Embedded Wallet address.
 *
 * The SERVER names the recipient from the session (c-125): the caller's other
 * participant in it. The app never says who; a store slice can hold someone
 * else by the time the tip opens (a ring accepted on the post-session screen).
 * Anything but a participant of a real 1:1 with a real second seat is a 403
 * (backlog c-008).
 */
export const getEmbeddedRecipientWallet = async (
  sessionId: number,
): Promise<GetEmbeddedRecipientWalletResponse> => {
  try {
    const response = await api.get('/wallet/embedded/recipient', {
      params: { session_id: sessionId },
    });
    return {
      success: true,
      has_wallet: response.data.has_wallet,
      wallet_address: response.data.wallet_address,
      recipient: response.data.recipient ?? null,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { error?: string; has_wallet?: boolean; recipient?: TipRecipient | null };
        };
      };
      return {
        success: false,
        error: serverErrorMessage(axiosError.response?.data) ?? 'Failed to fetch recipient wallet',
        has_wallet: axiosError.response?.data?.has_wallet,
        recipient: axiosError.response?.data?.recipient ?? null,
        status: axiosError.response?.status,
      };
    }
    return { success: false, error: handleAxiosError(error, 'Failed to fetch recipient wallet') };
  }
};

// ============================================
// Tipped Queue Progress
// ============================================

export interface TippedQueueProgress {
  seconds_completed: number;
  seconds_required: number;
  hours_completed: number;
  hours_required: number;
  time_percentage: number;
  sessions_completed: number;
  sessions_required: number;
  sessions_percentage: number;
  overall_percentage: number;
  unlocked: boolean;
  unlocked_at: string | null;
  blocking_factor: 'time' | 'sessions' | 'both' | null;
}

interface GetTippedQueueStatusSuccessResponse {
  success: true;
  progress: TippedQueueProgress;
  can_access_tipped_queue: boolean;
  tipped_empathy_enabled: boolean;
  current_queue_type: 'tipped' | 'untipped';
  has_embedded_wallet: boolean;
  pool_stats: {
    tipped: { givers_online: number; givers_ready: number; seekers_waiting: number };
    untipped: { givers_online: number; givers_ready: number; seekers_waiting: number };
  };
}

interface GetTippedQueueStatusErrorResponse {
  success: false;
  error: string;
}

type GetTippedQueueStatusResponse =
  | GetTippedQueueStatusSuccessResponse
  | GetTippedQueueStatusErrorResponse;

/**
 * Get tipped queue status and unlock progress for the current user.
 */
export const getTippedQueueStatus = async (): Promise<GetTippedQueueStatusResponse> => {
  try {
    const response = await api.get('/empathy-queue/tipped-status');
    if (response.data.status === 'success') {
      return {
        success: true,
        progress: response.data.progress,
        can_access_tipped_queue: response.data.can_access_tipped_queue,
        tipped_empathy_enabled: response.data.tipped_empathy_enabled,
        current_queue_type: response.data.current_queue_type,
        has_embedded_wallet: response.data.has_embedded_wallet,
        pool_stats: response.data.pool_stats,
      };
    }
    return {
      success: false,
      error: serverErrorMessage(response.data) ?? 'Failed to get tipped queue status',
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get tipped queue status') };
  }
};

// ============================================
// Tipped Empathy Toggle
// ============================================

interface ToggleTippedEmpathySuccessResponse {
  success: true;
  message: string;
  tipped_empathy_enabled: boolean;
  current_queue_type: 'tipped' | 'untipped';
}

interface ToggleTippedEmpathyErrorResponse {
  success: false;
  error: string;
}

type ToggleTippedEmpathyResponse =
  | ToggleTippedEmpathySuccessResponse
  | ToggleTippedEmpathyErrorResponse;

/**
 * Toggle tipped empathy enabled preference.
 * Only works if user has unlocked tipped queue access.
 */
export const toggleTippedEmpathy = async (
  enabled: boolean,
): Promise<ToggleTippedEmpathyResponse> => {
  try {
    const response = await api.post('/empathy-queue/tipped-empathy/toggle', { enabled });
    if (response.data.status === 'success') {
      return {
        success: true,
        message: response.data.message,
        tipped_empathy_enabled: response.data.tipped_empathy_enabled,
        current_queue_type: response.data.current_queue_type,
      };
    }
    return {
      success: false,
      error: serverErrorMessage(response.data) ?? 'Failed to toggle tipped empathy',
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to toggle tipped empathy') };
  }
};

// ============================================
// Dev/Test Endpoints (Local/Testing Only)
// ============================================

export interface DevUserWithWallet {
  id: number;
  name: string;
  username: string;
  wallet_address: string;
  avatar: string | null;
}

export interface DevUserWithoutWallet {
  id: number;
  name: string;
  username: string;
  avatar: string | null;
}

interface GetWalletTestUsersSuccessResponse {
  success: true;
  current_user: {
    id: number;
    name: string;
    username: string;
    wallet_address: string | null;
    avatar: string | null;
  };
  protocol_wallet: string;
  users_with_wallets: DevUserWithWallet[];
  users_without_wallets: DevUserWithoutWallet[];
}

interface GetWalletTestUsersErrorResponse {
  success: false;
  error: string;
}

type GetWalletTestUsersResponse =
  | GetWalletTestUsersSuccessResponse
  | GetWalletTestUsersErrorResponse;

/**
 * Get users list for wallet testing page.
 * DEV ONLY - only available in local/testing environments.
 */
export const getWalletTestUsers = async (): Promise<GetWalletTestUsersResponse> => {
  try {
    const response = await api.get('/dev/wallet-test-users');
    return {
      success: true,
      current_user: response.data.current_user,
      protocol_wallet: response.data.protocol_wallet,
      users_with_wallets: response.data.users_with_wallets,
      users_without_wallets: response.data.users_without_wallets,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get wallet test users') };
  }
};

interface CreateTestSessionSuccessResponse {
  success: true;
  session_id: number;
  is_existing: boolean;
  message: string;
}

interface CreateTestSessionErrorResponse {
  success: false;
  error: string;
}

type CreateTestSessionResponse = CreateTestSessionSuccessResponse | CreateTestSessionErrorResponse;

/**
 * Create a test empathy session for tipping testing.
 * DEV ONLY - only available in local/testing environments.
 */
export const createTestSession = async (
  recipientId: number,
): Promise<CreateTestSessionResponse> => {
  try {
    const response = await api.post('/dev/create-test-session', { recipient_id: recipientId });
    return {
      success: true,
      session_id: response.data.session_id,
      is_existing: response.data.is_existing,
      message: response.data.message,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to create test session') };
  }
};

// ============================================
// Tipped Queue Dev/Test Endpoints
// ============================================

interface TippedQueueProgressDevResponse {
  success: boolean;
  progress?: TippedQueueProgress;
  thresholds?: {
    seconds_required: number;
    hours_required: number;
    sessions_required: number;
    min_session_duration_seconds: number;
  };
  error?: string;
}

/**
 * Get current tipped queue progress (DEV ONLY)
 */
export const getDevTippedQueueProgress = async (): Promise<TippedQueueProgressDevResponse> => {
  try {
    const response = await api.get('/dev/tipped-queue-progress');
    return {
      success: true,
      progress: response.data.progress,
      thresholds: response.data.thresholds,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to get progress') };
  }
};

interface UnlockTippedQueueResponse {
  success: boolean;
  message?: string;
  unlocked_at?: string;
  error?: string;
}

/**
 * Force unlock tipped queue and trigger celebration (DEV ONLY)
 */
export const devUnlockTippedQueue = async (): Promise<UnlockTippedQueueResponse> => {
  try {
    const response = await api.post('/dev/unlock-tipped-queue');
    return {
      success: true,
      message: response.data.message,
      unlocked_at: response.data.unlocked_at,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to unlock') };
  }
};

interface ResetTippedQueueResponse {
  success: boolean;
  message?: string;
  previous_values?: {
    empathy_active_seconds: number;
    empathy_session_count: number;
    tipped_queue_unlocked: boolean;
    tipped_queue_unlocked_at: string | null;
  };
  error?: string;
}

/**
 * Reset tipped queue progress to zero (DEV ONLY)
 */
export const devResetTippedQueue = async (): Promise<ResetTippedQueueResponse> => {
  try {
    const response = await api.post('/dev/reset-tipped-queue');
    return {
      success: true,
      message: response.data.message,
      previous_values: response.data.previous_values,
    };
  } catch (error) {
    return { success: false, error: handleAxiosError(error, 'Failed to reset') };
  }
};
